import React, { useEffect } from 'react';
import styled from 'styled-components'
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer"

import KFLogo from '../../images/Kettleandfire-color.svg'
import BGimage from '../../images/kf/kf-section-bg.jpg'
import BGimageMobile from '../../images/kf/kf-section-bg-mobile.jpg'
import ImageLeft from '../../images/kf/kf-left-image.png'
import ImageCenter from '../../images/kf/kf-center-image.png'
import ImageRight from '../../images/kf/kf-right-image.png'

const Container = styled.section`
    width: 100%;
    padding: 72px 24px;
    background: #FAF9F5 url(${BGimageMobile}) no-repeat top center;
    background-size: cover;
    position: relative;
    overflow: hidden;

    .client-logo{
        display: block;
        margin: 0 auto;
        position: relative;
        z-index: 20;
    }

    figure{
        margin: 0;
        padding: 0;
    }

    .client-copy{
        margin: 32px auto 0;
        text-align: center;
        padding: 0;

        h2{
            font-size: 30px;
            line-height: 38px;
            font-weight: 600;
            letter-spacing: -0.15px;
            margin: 0 0 16px;
        }

        a{
            font-size: 16px;
            line-height: 1;
            color: #131217;

            &:hover{
                text-decoration: none;
            }
        }
    }

    @media (min-width: 768px){
        padding: 138px 0 128px;
        background: #FAF9F5 url(${BGimage}) no-repeat top center;
        background-size: cover;

        .client-logo{
            max-width: 327px;
            width: 100%;
        }

        .client-copy{
            max-width: 670px;
            width: 100%;
            margin: 0 auto;
            padding: 0;

            h2{
                font-size: 40px;
                line-height: 48px;
                letter-spacing: -0.2px;
                margin: 0 0 24px;
            }
        }
    }
`;

const DesktopImages = styled.div`
    position: relative;
    max-width: 1248px;
    width: 100%;
    height: 100%;
    padding: 0 24px 56px;
    margin: 96px auto 0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .product-packaging{
        max-width: 1008px;
        margin: 0 auto;
        display: flex;
        justify-content: center;
        align-items: flex-end;
    }

    .product-left{
        position: relative;
        max-width: 297px;
        width: 100%;
        z-index:1;
        transform-origin: bottom right;
    }
    .product-center{
        position: relative;
        max-width: 342px;
        width: 100%;
        z-index: 2;
        transform-origin: bottom center;
    }
    .product-right{
        position: relative;
        max-width: 305px;
        width: 100%;
        z-index: 1;
        transform-origin: bottom left;
    }

    @media (max-width: 768px){
        display: none;
    }
`;

const MobileImage = styled.div`
    display: none;

    @media (max-width: 768px){
        display: block;
        position: relative;
        padding: 48px 0 24px;

        .product-packaging{
            max-width: 1008px;
            margin: 0 auto;
            display: flex;
            justify-content: center;
            align-items: flex-end;
        }
    
        .product-left{
            position: relative;
            max-width: 108px;
            width: 100%;
            z-index: 10;
            transform-origin: bottom right;
        }
        .product-center{
            position: relative;
            max-width: 124px;
            width: 100%;
            z-index: 12;
            transform-origin: bottom center;
        }
        .product-right{
            position: relative;
            max-width: 111px;
            width: 100%;
            z-index: 10;
            transform-origin: bottom left;
        }
    }
`;


const KettleandFire = () => {
    const animation = useAnimation()
    const [Kf, inView] = useInView({
        triggerOnce: true,
    })

    useEffect(() => {
        if (inView) {
          animation.start("visible")
        }
    }, [animation, inView])

    return (
        <Container ref={Kf}>
            <img src={KFLogo} alt="Kettle and Fire" className="client-logo"/>

            <DesktopImages>
                <motion.div className="product-packaging">
                    <motion.img className="product-left" src={ImageLeft} alt="" 
                        animate={animation}
                        initial="hidden"
                        variants={{
                            visible: {
                                opacity: 1,
                                y: 24,
                                transition: { duration: 0.8, delay: 0.4, ease: "easeInOut" },
                            },
                            hidden: { 
                                opacity: 0, 
                                y: 50,
                                x: 24
                            },
                        }}
                    />
                    <motion.img className="product-center" src={ImageCenter} alt="" 
                        animate={animation}
                        initial="hidden"
                        variants={{
                            visible: {
                                opacity: 1,
                                y: 0,
                                transition: { duration: 0.8, delay: 0.3, ease: "easeInOut" },
                            },
                            hidden: { 
                                opacity: 0, 
                                y: 50,
                            },
                        }}
                    />
                    <motion.img className="product-right" src={ImageRight} alt=""
                        animate={animation}
                        initial="hidden"
                        variants={{
                            visible: {
                                opacity: 1,
                                y: 32,
                                transition: { duration: 0.8, delay: 0.5, ease: "easeInOut" },
                            },
                            hidden: { 
                                opacity: 0, 
                                y: 50,
                                x: -16
                            },
                        }} 
                    />
                </motion.div>
            </DesktopImages>

            <MobileImage>
                <motion.div className="product-packaging">
                    <motion.img className="product-left" src={ImageLeft} alt="" 
                        animate={animation}
                        initial="hidden"
                        variants={{
                            visible: {
                                opacity: 1,
                                y: 24,
                                transition: { duration: 0.8, delay: 0.3, ease: "easeInOut" },
                            },
                            hidden: { 
                                opacity: 0, 
                                y: 50,
                                x: 0
                            },
                        }}
                    />
                    <motion.img className="product-center" src={ImageCenter} alt="" 
                        animate={animation}
                        initial="hidden"
                        variants={{
                            visible: {
                                opacity: 1,
                                y: 0,
                                transition: { duration: 0.8, delay: 0.2, ease: "easeInOut" },
                            },
                            hidden: { 
                                opacity: 0, 
                                y: 50,
                            },
                        }}
                    />
                    <motion.img className="product-right" src={ImageRight} alt=""
                        animate={animation}
                        initial="hidden"
                        variants={{
                            visible: {
                                opacity: 1,
                                y: 32,
                                transition: { duration: 0.8, delay: 0.4, ease: "easeInOut" },
                            },
                            hidden: { 
                                opacity: 0, 
                                y: 50,
                                x: 0
                            },
                        }} 
                    />
                </motion.div>
            </MobileImage>

            <div className="client-copy">
                <h2>Internet’s favorite bone broth brand</h2>
                <a href="https://www.kettleandfire.com/?ref=Wayfx" target="_blank" rel="noopener noreferrer">Visit kettleandfire.com</a>
            </div>
            
        </Container>
    )
}

export default KettleandFire